const COINGECKO_BASE_URL = "/api/coingecko/api/v3";
const COINMARKETCAP_URL = "/api/coinmarketcap/v1/cryptocurrency/quotes/latest";

const COINS_LIST_TTL_MS = 60 * 60 * 1000;
const coinsListCache = {
  data: null,
  fetchedAt: 0,
};

const PREFERRED_IDS = {
  BTC: "bitcoin",
  ETH: "ethereum",
  SOL: "solana",
  DOGE: "dogecoin",
  BNB: "binancecoin",
  XRP: "ripple",
  ADA: "cardano",
  AVAX: "avalanche-2",
  DOT: "polkadot",
  LINK: "chainlink",
  LTC: "litecoin",
  BCH: "bitcoin-cash",
  NEAR: "near",
  UNI: "uniswap",
  SUI: "sui",
  APT: "aptos",
  ATOM: "cosmos",
  OP: "optimism",
  FIL: "filecoin",
  XLM: "stellar",
  TRX: "tron",
  AAVE: "aave",
  ETC: "ethereum-classic",
  ALGO: "algorand",
  FTM: "fantom",
  EOS: "eos",
  EGLD: "elrond-erd-2",
  RUNE: "thorchain",
  CHZ: "chiliz",
  MATIC: "polygon-ecosystem-token",
  POL: "polygon-ecosystem-token",
  WLD: "worldcoin-wld",
  WIF: "dogwifcoin",
  SAND: "the-sandbox",
  MANA: "decentraland",
  AXS: "axie-infinity",
  ENS: "ethereum-name-service",
  GMT: "stepn",
  ARB: "arbitrum",
};

const getCoinsList = async () => {
  const now = Date.now();
  if (coinsListCache.data && now - coinsListCache.fetchedAt < COINS_LIST_TTL_MS) {
    return coinsListCache.data;
  }
  const response = await fetch(`${COINGECKO_BASE_URL}/coins/list?include_platform=false`);
  if (!response.ok) {
    throw new Error(`CoinGecko coins list failed: ${response.status}`);
  }
  const data = await response.json();
  coinsListCache.data = Array.isArray(data) ? data : [];
  coinsListCache.fetchedAt = now;
  return coinsListCache.data;
};

const resolveCoinId = (symbol, coinsList) => {
  const upper = String(symbol || "").toUpperCase();
  if (!upper) return null;
  if (PREFERRED_IDS[upper]) return PREFERRED_IDS[upper];
  const matches = coinsList.filter(
    (coin) => String(coin.symbol || "").toUpperCase() === upper
  );
  if (!matches.length) return null;
  const exactName = matches.find(
    (coin) => String(coin.name || "").toUpperCase() === upper
  );
  return (exactName || matches[0]).id || null;
};

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const fetchCoinGeckoMarkets = async (symbols) => {
  const coinsList = await getCoinsList();
  const idMap = new Map();
  const idToSymbol = new Map();
  const missing = [];

  symbols.forEach((symbol) => {
    const id = resolveCoinId(symbol, coinsList);
    if (id) {
      idMap.set(symbol, id);
      if (!idToSymbol.has(id)) {
        idToSymbol.set(id, symbol);
      }
    } else {
      missing.push(symbol);
    }
  });

  const ids = Array.from(new Set(idMap.values()));
  if (!ids.length) {
    return { data: {}, missing };
  }

  const params = new URLSearchParams({
    vs_currency: "usd",
    ids: ids.join(","),
    sparkline: "false",
    price_change_percentage: "24h",
  });
  const response = await fetch(`${COINGECKO_BASE_URL}/coins/markets?${params}`);
  if (!response.ok) {
    throw new Error(`CoinGecko markets failed: ${response.status}`);
  }

  const rows = await response.json();
  const data = {};
  if (Array.isArray(rows)) {
    rows.forEach((row) => {
      const id = row.id;
      const mappedSymbol = idToSymbol.get(id);
      if (!mappedSymbol) return;
      data[mappedSymbol] = {
        id,
        market_cap: row.market_cap ?? null,
        total_volume: row.total_volume ?? null,
        fully_diluted_valuation: row.fully_diluted_valuation ?? null,
      };
    });
  }

  return { data, missing };
};

const fetchCoinMarketCapData = async (symbols) => {
  if (!symbols.length) return {};

  const params = new URLSearchParams({
    symbol: symbols.join(","),
    convert: "USD",
  });
  const response = await fetch(`${COINMARKETCAP_URL}?${params}`, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    console.warn("CoinMarketCap fallback failed:", response.status);
    return {};
  }

  const payload = await response.json();
  const data = payload?.data || {};
  const result = {};

  const pickBestEntry = (entries) => {
    if (!entries.length) return null;
    return entries.reduce((best, current) => {
      if (!best) return current;
      const bestQuote = best?.quote?.USD || {};
      const currentQuote = current?.quote?.USD || {};
      const bestMetric =
        Number(bestQuote.market_cap) ||
        Number(bestQuote.fully_diluted_market_cap) ||
        Number(bestQuote.volume_24h) ||
        0;
      const currentMetric =
        Number(currentQuote.market_cap) ||
        Number(currentQuote.fully_diluted_market_cap) ||
        Number(currentQuote.volume_24h) ||
        0;
      return currentMetric > bestMetric ? current : best;
    }, null);
  };

  Object.entries(data).forEach(([symbol, entry]) => {
    const entries = Array.isArray(entry) ? entry : [entry];
    const chosen = pickBestEntry(entries);
    if (!chosen) return;
    const quote = chosen?.quote?.USD || {};
    const price = safeNumber(quote.price);
    const circulating = safeNumber(chosen.circulating_supply);
    const totalSupply = safeNumber(chosen.total_supply);
    const maxSupply = safeNumber(chosen.max_supply);
    const marketCapFromSupply =
      price != null && circulating != null ? price * circulating : null;
    const fdvFromSupply =
      price != null && (maxSupply != null || totalSupply != null)
        ? price * (maxSupply ?? totalSupply)
        : null;

    const computedMarketCap =
      safeNumber(quote.market_cap) ||
      safeNumber(marketCapFromSupply) ||
      safeNumber(quote.fully_diluted_market_cap) ||
      safeNumber(fdvFromSupply) ||
      null;
    const computedFdv =
      safeNumber(quote.fully_diluted_market_cap) ||
      safeNumber(fdvFromSupply) ||
      null;

    result[symbol.toUpperCase()] = {
      id: `coinmarketcap:${chosen?.id ?? symbol}`,
      market_cap: computedMarketCap,
      total_volume: safeNumber(quote.volume_24h),
      fully_diluted_valuation: computedFdv,
    };
  });

  return result;
};

export const fetchMarketData = async (symbols) => {
  const normalized = symbols
    .map((symbol) => String(symbol || "").toUpperCase())
    .filter(Boolean);
  const unique = Array.from(new Set(normalized));
  if (!unique.length) {
    return { data: {}, missing: [] };
  }

  const { data, missing } = await fetchCoinGeckoMarkets(unique);
  const fallbackSymbols = unique.filter((symbol) => {
    const entry = data[symbol];
    if (!entry) return true;
    return (
      entry.market_cap == null ||
      entry.market_cap === 0 ||
      entry.total_volume == null ||
      entry.total_volume === 0 ||
      entry.fully_diluted_valuation == null ||
      entry.fully_diluted_valuation === 0
    );
  });

  if (fallbackSymbols.length) {
    const cmcData = await fetchCoinMarketCapData(fallbackSymbols);
    fallbackSymbols.forEach((symbol) => {
      const fallback = cmcData[symbol];
      if (!fallback) return;
      const existing = data[symbol] || {};
      data[symbol] = {
        ...existing,
        market_cap: fallback.market_cap ?? existing.market_cap ?? null,
        total_volume: fallback.total_volume ?? existing.total_volume ?? null,
        fully_diluted_valuation:
          fallback.fully_diluted_valuation ??
          existing.fully_diluted_valuation ??
          null,
        id: existing.id || fallback.id,
      };
    });
  }

  return { data, missing };
};
